import { createContext, useContext, useEffect, useRef, useState } from "react";

import {
  Modal,
  type ModalHeaderProps,
  type ModalProps,
} from "semantic-ui-react";

import { uuidv4 } from "simplydo/core/helpers";

import {
  FocusStackContext,
  createFocusTrap,
  useFocusStack,
} from "./FocusStack";

const AccessibleModalContext = createContext({ titleId: "" });

const AccessibleModal = (props: ModalProps) => {
  const focusStack = useFocusStack();

  const modalRef = useRef<HTMLDivElement>(null);
  const [returnFocusElement, setReturnFocusElement] =
    useState<HTMLElement | null>(null);

  const [internalOpen, setInternalOpen] = useState(props.open ?? false);
  const [titleId] = useState(uuidv4());

  // When the modal is opened, either controlled or uncontrolled, retain state of the current focus and set focus to the first focusable child
  // Within the modal, trap focus to prevent focus from leaving the modal
  // Once closed, return focus to the previously focused element
  useEffect(() => {
    if (
      props.open !== undefined ? props.open === true : internalOpen === true
    ) {
      setReturnFocusElement(document.activeElement as HTMLElement);

      const focusTrap = createFocusTrap(modalRef.current);
      focusStack.insertFocusTrap(focusTrap);

      return () => {
        focusStack.removeFocusTrap(focusTrap);
      };
    }
  }, [props.open, focusStack, internalOpen]);

  useEffect(() => {
    if (
      props.open !== undefined ? props.open === false : internalOpen === false
    ) {
      if (returnFocusElement && document.activeElement !== returnFocusElement) {
        returnFocusElement.focus();
      }
    }
  }, [props.open, internalOpen, returnFocusElement]);

  return (
    <FocusStackContext.Provider value={focusStack}>
      <AccessibleModalContext.Provider value={{ titleId }}>
        <Modal
          role="dialog"
          aria-modal="true"
          mountNode={document.getElementById("semantic-modal-mount-node")}
          aria-labelledby={titleId}
          tabIndex={-1}
          closeOnEscape
          ref={modalRef}
          {...props}
          onOpen={(e, data) => {
            // Only triggers if modal is uncontrolled (no open prop)
            setInternalOpen(true);
            props.onOpen?.(e, data);
          }}
          onClose={(e, data) => {
            // Only triggers if modal is uncontrolled (no open prop)
            setInternalOpen(false);
            props.onClose?.(e, data);
          }}
        />
      </AccessibleModalContext.Provider>
    </FocusStackContext.Provider>
  );
};

const AccessibleModalHeader = (props: ModalHeaderProps) => {
  const { titleId } = useContext(AccessibleModalContext);
  return <Modal.Header id={titleId} {...props} />;
};

AccessibleModal.Header = AccessibleModalHeader;
AccessibleModal.Content = Modal.Content;
AccessibleModal.Actions = Modal.Actions;
AccessibleModal.Dimmer = Modal.Dimmer;
AccessibleModal.Description = Modal.Description;

export { AccessibleModal };
